import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import PageBackground from 'components/v2/PageBackground';
import { RetainerDocumentPanel } from '../components/retainerDocumentPanel';

import api from 'api';
import { getRetainerTypeLabelKey } from 'helpers/applicationRetainers';
import {
    useApplicationRetainerDocument,
    useInvalidateApplicationRetainers,
} from 'hooks/useApplicationRetainers';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadApplications } from 'redux/applications/applications-middleware';
import type { AppDispatch, RootState } from 'redux/store';
import { isAgreementSigningAvailable, toClientSignatureHtml } from 'utils/agreement';
import { getErrorMessage, getErrorStatus } from 'utils/v2/errorHelper';

const RATE_LIMIT_WINDOW_MS = 60_000;

/**
 * `/visa-process/:id/agreement/:agreementId` — deep link from the retainer
 * email / notification. Signed retainers show the stored PDF; unsigned ones
 * render the live PDF with a signature pad.
 */
const SignRetainerDocument = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const params = useParams();
    const { translate } = useTranslate();

    const applicationId = Number(params.id);
    const agreementId = Number(params.agreementId);
    const hasValidIds =
        Number.isFinite(applicationId) &&
        applicationId > 0 &&
        Number.isFinite(agreementId) &&
        agreementId > 0;

    const user = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const { applications } = useSelector((state: RootState) => state.applications);
    const applicationsUserId = selectedUserId || user?.id;

    const invalidateRetainers = useInvalidateApplicationRetainers();
    const { data, isLoading, isError, error, refetch } = useApplicationRetainerDocument(
        hasValidIds ? applicationId : null,
        hasValidIds ? agreementId : null
    );

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rateLimited, setRateLimited] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [candidateId, setCandidateId] = useState<number | null>(null);
    const rateLimitTimer = useRef<number | null>(null);

    useEffect(() => {
        if (isError) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    }, [dispatch, error, isError, translate]);

    // Only the case candidate may sign; the sidebar's applications tell us who that is
    useEffect(() => {
        const fromStore = applications.find((app) => app.id === applicationId);
        if (fromStore) {
            setCandidateId(fromStore.candidate_id);
            return undefined;
        }
        if (!hasValidIds) return undefined;
        let cancelled = false;
        api.application
            .get(String(applicationId))
            .then((response) => {
                if (!cancelled) setCandidateId(response.data?.candidate_id ?? null);
            })
            .catch(() => {
                if (!cancelled) setCandidateId(null);
            });
        return () => {
            cancelled = true;
        };
    }, [applicationId, applications, hasValidIds]);

    useEffect(
        () => () => {
            if (rateLimitTimer.current) window.clearTimeout(rateLimitTimer.current);
        },
        []
    );

    const startRateLimitWindow = () => {
        setRateLimited(true);
        if (rateLimitTimer.current) window.clearTimeout(rateLimitTimer.current);
        rateLimitTimer.current = window.setTimeout(
            () => setRateLimited(false),
            RATE_LIMIT_WINDOW_MS
        );
    };

    const handleSign = async (signature: string) => {
        if (!hasValidIds || !isAgreementSigningAvailable(data?.retainer)) return;
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('client_signature', toClientSignatureHtml(signature));
            await api.applicationRetainers.sign(applicationId, agreementId, formData);
            dispatch(setAlert({ message: translate('retainerSigned'), type: 'success' }));
            invalidateRetainers(applicationId);
            if (applicationsUserId) {
                void dispatch(loadApplications(String(applicationsUserId)));
            }
            await refetch();
        } catch (signError) {
            const status = getErrorStatus(signError);
            const message = getErrorMessage(signError, translate('somethingWentWrong'));
            if (status === 409) {
                // Already signed elsewhere — reload as the signed copy
                dispatch(setAlert({ message, type: 'info' }));
                invalidateRetainers(applicationId);
                await refetch();
            } else if (status === 429) {
                dispatch(setAlert({ message, type: 'warning' }));
                startRateLimitWindow();
            } else if (status === 403) {
                dispatch(setAlert({ message, type: 'error' }));
                setForbidden(true);
            } else {
                dispatch(setAlert({ message, type: 'error' }));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const retainer = data?.retainer ?? null;
    const canSign = !forbidden && (candidateId == null || candidateId === user?.id);
    const title = retainer
        ? translate(getRetainerTypeLabelKey(retainer.agreement_type))
        : translate('retainer');

    return (
        <PageBackground rootSx={{ overflow: 'auto' }}>
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.35rem', sm: '1.5rem' },
                        letterSpacing: '-0.02em',
                        lineHeight: 1.25,
                        mb: 0.5,
                    }}
                >
                    {translate('signRetainerAgreements')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {translate('caseRetainerClientDescription')}
                </Typography>
            </Box>

            <RetainerDocumentPanel
                retainer={retainer}
                document={data?.document ?? null}
                title={title}
                loading={hasValidIds && isLoading}
                canSign={canSign}
                isSubmitting={isSubmitting}
                rateLimited={rateLimited}
                onBack={() => navigate(`/visa-process/${applicationId}/agreement`)}
                onSign={(signature) => void handleSign(signature)}
            />
        </PageBackground>
    );
};

export default SignRetainerDocument;
